import FormData from '@/classes/form/FormData'
import FormDescriptor from '@/classes/form/FormDescriptor'
import caseApi from '@/api/case/caseApi'
import certificateApi from '@/api/certificate/certificateApi'
import { Case } from '@/classes/case/Case'
import { Recommendation } from '@/classes/case/Recommendation'
import { findItem, getFilter } from '@/store/utility_functions'

const DEFAULT_SORT = 'case_id'

const state = {
  cases: [],
  closeCaseFormData: [],
  closeCaseFormDescriptor: []
}

const getters = {
  getCaseForId: state => (caseId) => {
    return findItem(state.cases, caseId)
  }
}

const mutations = {
  setStateCases: (state, kases) => {
    state.cases = kases
  },
  addToStateCases: (state, kase) => {
    state.cases.push(kase)
  },
  updateStateCases: (state, data) => {
    data.current.updateCurrentCase(data.updated)
  },
  setStateCloseCaseFormDescriptors: (state, data) => {
    state.closeCaseFormDescriptor = FormDescriptor.asFormDescriptors(data)
  },
  setStateCloseCaseFormDatas: (state, data) => {
    state.closeCaseFormData = FormData.initiateFormDatas(state.closeCaseFormDescriptor)
    FormData.loadInitialFormDatas(data, state.closeCaseFormData)
    FormDescriptor.checkFormDescriptorsDependencies(state.closeCaseFormDescriptor, state.closeCaseFormData)
  }
}

const actions = {
  getCases ({ commit }, request) {
    request.ordering = request.ordering || DEFAULT_SORT
    let filter = getFilter(request)
    return caseApi.getCases(filter).then((response) => {
      let kases = Case.asCases(response.data.results)
      commit('setStateCases', kases)
      return response.data
    }).catch((err) => {
      throw err
    })
  },
  setCase ({ commit, state }, caseId) {
    return caseApi.getCase(caseId).then((response) => {
      let kase = Case.asCase(response.data)
      let existingItem = findItem(state.cases, kase.id)
      if (existingItem) {
        commit('updateStateCases', {
          current: existingItem,
          updated: kase
        })
        return existingItem
      }
      commit('addToStateCases', kase)
      return kase
    }).catch((err) => {
      throw err
    })
  },
  setCaseForReview ({ commit, state }, caseId) {
    return caseApi.getCaseForReview(caseId).then((response) => {
      let kase = Case.asCase(response.data)
      let existingItem = findItem(state.cases, kase.id)
      if (existingItem) {
        commit('updateStateCases', {
          current: existingItem,
          updated: kase
        })
        return existingItem
      }
      commit('addToStateCases', kase)
      return kase
    }).catch((err) => {
      throw err
    })
  },
  updateCase ({ commit }, data) {
    return caseApi.patchCase(data.caseId, data.payload).then((response) => {
      commit('updateStateCases', {
        current: findItem(state.cases, data.caseId),
        updated: Case.asCase(response.data)
      })
    }).catch((err) => {
      throw err
    })
  },
  claimCase ({ commit }, data) {
    return caseApi.claimCase(data.caseId).catch((err) => {
      throw err
    })
  },
  clearResponsible ({ commit }, caseId) {
    return caseApi.clearResponsible(caseId).then((response) => {
    }).catch((err) => {
      throw err
    })
  },
  setLegacyInvoiced ({ commit }, caseId) {
    return caseApi.setLegacyInvoiced(caseId).then((response) => {
    }).catch((err) => {
      throw err
    })
  },
  revertCaseToReviewer ({ commit }, caseId) {
    return caseApi.revertCaseToReviewer(caseId).then((response) => {
    }).catch((err) => {
      throw err
    })
  },
  setupRejectCloseCaseForm ({ commit }, caseId) {
    return caseApi.getCaseCloseForm(caseId).then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateCloseCaseFormDescriptors', [response.data])
      commit('setStateCloseCaseFormDatas', formData)
    }).catch((err) => {
      throw err
    })
  },
  postCaseApproveForm ({ commit }, caseId) {
    return caseApi.postCaseApproveForm(caseId, {}).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  saveCloseCaseForm ({ commit }, requestData) {
    return caseApi.postCaseCloseForm(requestData.data.caseId, requestData.serializedData).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  setupRecommendCaseForm ({ commit }, caseId) {
    return caseApi.getRecommendCaseCloseForm(caseId).then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateCloseCaseFormDescriptors', [response.data])
      commit('setStateCloseCaseFormDatas', formData)
    }).catch((err) => {
      throw err
    })
  },
  saveRecommendCaseForm ({ commit }, requestData) {
    return caseApi.postRecommendCaseForm(requestData.data.caseId, requestData.serializedData, requestData.data.type).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  getRecommendationComment ({ commit }, caseId) {
    return caseApi.getRecommendationComment(caseId).then((response) => {
      return Recommendation.asRecommendations(response.data.results)
    }).catch((err) => {
      throw err
    })
  },
  clearCases ({ commit }) {
    commit('setStateCases', [])
  },
  clearCaseFormData ({ commit }) {
    commit('setStateCloseCaseFormDescriptors', [])
    commit('setStateCloseCaseFormDatas', [])
  },
  createRenewalCase ({ commit }, certificateId) {
    return certificateApi.postCreateRenewalCase(certificateId).then((response) => {
      let kase = Case.asCase(response.data)
      commit('addToStateCases', kase)
      return kase
    }).catch((err) => {
      throw err
    })
  },
  setCaseController ({ commit }, caseId) {
    return caseApi.setCaseController(caseId).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  setCaseNotInvoiceable ({ commit }, caseId) {
    return caseApi.setCaseNotInvoiceable(caseId).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  setCaseInvoiceable ({ commit }, caseId) {
    return caseApi.setCaseInvoiceable(caseId).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
